import { useState } from "react";
import {
    Box,
    Button,
    Divider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import {useSearchParams} from "react-router";
pdfMake.vfs = pdfFonts.vfs;

interface FormData {
    name: string;
    surname: string;
    age: string;
    city: string;
    phoneNumber: string;
    email: string;
    languageExperience: string;
    medicalStatus: string;
    diet: string;
    dailyTreatment: string;
    parentName: string;
    parentSurname: string;
    parentPhone: string;
    parentEmail: string;
}

type FormErrors = Record<keyof FormData, string>;

const initialForm: FormData = {
    name: "",
    surname: "",
    age: "",
    city: "",
    phoneNumber: "",
    email: "",
    languageExperience: "",
    medicalStatus: "",
    diet: "",
    dailyTreatment: "",
    parentName: "",
    parentSurname: "",
    parentPhone: "",
    parentEmail: "",
};

const initialErrors: FormErrors = Object.fromEntries(
    Object.keys(initialForm).map((k) => [k, ""])
) as FormErrors;

const requiredFields: (keyof FormData)[] = [
    "name", "surname", "age", "city",
    "parentName", "parentSurname", "parentPhone", "parentEmail",
];

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^[0-9+\s\-().]{7,20}$/;

const validate = (form: FormData): { errors: FormErrors; valid: boolean } => {
    const errors: FormErrors = { ...initialErrors };
    let valid = true;

    requiredFields.forEach((field) => {
        if (!form[field].trim()) {
            errors[field] = "Câmp obligatoriu";
            valid = false;
        }
    });

    if (form.age && (isNaN(Number(form.age)) || +form.age < 5 || +form.age > 99)) {
        errors.age = "Vârstă invalidă (5–99)";
        valid = false;
    }

    if (form.email && !emailRegex.test(form.email)) {
        errors.email = "Email invalid";
        valid = false;
    }

    if (form.parentEmail && !emailRegex.test(form.parentEmail)) {
        errors.parentEmail = "Email invalid";
        valid = false;
    }

    if (form.phoneNumber && !phoneRegex.test(form.phoneNumber)) {
        errors.phoneNumber = "Număr de telefon invalid";
        valid = false;
    }

    if (form.parentPhone && !phoneRegex.test(form.parentPhone)) {
        errors.parentPhone = "Număr de telefon invalid";
        valid = false;
    }

    return { errors, valid };
};

const RegistrationForm = () => {
    const [form, setForm] = useState<FormData>(initialForm);
    const [errors, setErrors] = useState<FormErrors>(initialErrors);
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const trip = searchParams.get("trip")?.replaceAll("_", " ");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const updatedForm = { ...form, [name]: value };
        setForm(updatedForm);
        if (errors[name as keyof FormData]) {
            const { errors: newErrors } = validate(updatedForm);
            setErrors((prev) => ({ ...prev, [name]: newErrors[name as keyof FormData] }));
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { errors: newErrors } = validate(form);
        setErrors((prev) => ({ ...prev, [e.target.name]: newErrors[e.target.name as keyof FormData] }));
    };

    const generateRegistrationDocument = (data: FormData) => {
        const bold = (text: string) => ({ text, bold: true });
        const cell = (text: string, opts = {}) => ({ text, ...opts });

        const docDefinition: any = {
            content: [
                { text: "FORMULAR DE ÎNSCRIERE", style: "header", alignment: "center" },
                { text: "TABĂRA CHIRON", style: "header", alignment: "center" },
                { text: trip?.toUpperCase(), style: "header", alignment: "center" },
                { text: "\n" },
                {
                    table: {
                        widths: [80, "*", 70, "*"],
                        body: [
                            [{ text: "DATE PARTICIPANT:", colSpan: 4, alignment: "center", bold: true }, {}, {}, {}],
                            [bold("NUME:"), data.surname, bold("PRENUME:"), data.name],
                            [bold("VÂRSTA:"), data.age, bold("ORAȘ:"), data.city],
                            [bold("NR. TEL."), data.phoneNumber, bold("E-MAIL"), data.email],
                            [{ text: "EXPERIENȚĂ\nÎN STUDIUL\nLIMBILOR", bold: true }, { text: data.languageExperience, colSpan: 3 }, {}, {}],
                            [{ text: "SUFERINȚE\nMEDICALE\n\nALERGII", bold: true }, { text: data.medicalStatus, colSpan: 3 }, {}, {}],
                            [{ text: "REGIM\nALIMENTAR", bold: true }, { text: data.diet, colSpan: 3 }, {}, {}],
                            [{ text: "TRATAMENT\nADMINISTRAT\nZILNIC", bold: true }, { text: data.dailyTreatment, colSpan: 3 }, {}, {}],
                            [{ text: "DATE PĂRINTE / TUTORE LEGAL", colSpan: 4, alignment: "center", bold: true }, {}, {}, {}],
                            [bold("NUME:"), data.parentSurname, bold("PRENUME:"), data.parentName],
                            [bold("NR. TEL."), data.parentPhone, bold("E-MAIL"), data.parentEmail],
                        ],
                    },
                    layout: {
                        hLineColor: () => "#aaaaaa",
                        vLineColor: () => "#aaaaaa",
                    },
                },
                { text: "\n\n" },
                {
                    text: [
                        "Subsemnatul/Subsemnata,.....................................................................................................",
                        "născută la data de ................................, în localitatea ...............................................................,",
                        "prin prezenta îmi exprim acordul cu privire la utilizarea și prelucrarea datelor cu caracter personal, conform Regulamentului",
                        "(UE) 2016/679 al Parlamentului European și al Consiliului din 27 aprilie 2016 privind protecția persoanelor fizice în ceea ce",
                        "privește prelucrarea datelor cu caracter personal și privind libera circulație a acestor date de către S.C. AVENTURĂ ȘI CĂLĂTORII S.R.L.."
                    ],
                    lineHeight: 1.8,
                },
                { text: "\n\n" },
                {
                    columns: [
                        { text: "Data:", width: "*" },
                        { text: "Semnătura:", width: "*", alignment: "right" },
                    ],
                },
            ],
            styles: {
                header: { fontSize: 14, bold: true, marginBottom: 2 },
            },
            defaultStyle: {
                fontSize: 10,
            },
        };

        pdfMake.createPdf(docDefinition).download(`inscriere_${data.name}_${data.surname}.pdf`);
    };

    const handleSubmit = () => {
        setSubmitted(true);
        const { errors: newErrors, valid } = validate(form);
        setErrors(newErrors);
        if (valid) generateRegistrationDocument(form);
    };

    const field = (
        name: keyof FormData,
        label: string,
        props: Partial<React.ComponentProps<typeof TextField>> = {}
    ) => (
        <TextField
            name={name}
            label={label}
            value={form[name]}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!errors[name]}
            helperText={errors[name]}
            fullWidth
            required={requiredFields.includes(name)}
            {...props}
        />
    );

    return (
        <Stack spacing={4} sx={{ p: 3, maxWidth: 700 }}>
            <Typography variant="h4" fontWeight="medium">
                Formular de Înscriere
            </Typography>
            <Typography variant="h5" fontWeight="medium">
                {trip}
            </Typography>

            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Date participant
                </Typography>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        {field("name", "Prenume")}
                        {field("surname", "Nume")}
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        {field("age", "Vârstă", { type: "number", sx: { maxWidth: 150 } })}
                        {field("city", "Oraș")}
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        {field("phoneNumber", "Telefon")}
                        {field("email", "Email", { type: "email" })}
                    </Stack>
                </Stack>
            </Box>

            <Divider />

            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Informații medicale și alimentare
                </Typography>
                <Stack spacing={2}>
                    {field("medicalStatus", "Stare medicală", { multiline: true, rows: 2 })}
                    {field("diet", "Regim alimentar", { multiline: true, rows: 2 })}
                    {field("dailyTreatment", "Tratament zilnic", { multiline: true, rows: 2 })}
                    {field("languageExperience", "Experiență lingvistică", { multiline: true, rows: 2 })}
                </Stack>
            </Box>

            <Divider />

            <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    Date părinte / tutore
                </Typography>
                <Stack spacing={2}>
                    <Stack direction="row" spacing={2}>
                        {field("parentName", "Prenume părinte")}
                        {field("parentSurname", "Nume părinte")}
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        {field("parentPhone", "Telefon părinte")}
                        {field("parentEmail", "Email părinte", { type: "email" })}
                    </Stack>
                </Stack>
            </Box>

            <Typography variant="caption" color="text.secondary">
                Câmpurile marcate cu * sunt obligatorii, restul sunt recomandate
            </Typography>

            <Button
                variant="contained"
                size="large"
                onClick={handleSubmit}
                sx={{ alignSelf: "flex-start", color: "white" }}
            >
                Generează document
            </Button>
        </Stack>
    );
};

export default RegistrationForm;